import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
//apis===================
import { getAllSites, networkWideSync, syncFvpBundle } from '../../api/fvpDoc';
//mui imports====
import clsx from 'clsx';
import { Box } from '@mui/system';
import useStyles from './styles';
import useCommonStyles from '../../commonStyles';
import { Chip, Paper } from '@mui/material';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import GeneralButton from '../../Components/GeneralButton';
import { UserContext } from '../../Contexts/UserContext';
import { toast } from 'react-toastify';
import FilterCustomTable from './Components/CustomFilterTable';
import PageHeading from '../../Components/PageHeading';

const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

const FvpSiteSearch = () => {
	const [sites, setSites] = useState([]);
	const { user } = useContext(UserContext);
	const [sitesLoaded, setSitesLoaded] = useState(false);
	const classes = useStyles();
	const commonStylesClasses = useCommonStyles();
	const navigate = useNavigate();

	//===============USE EFFECTS==================
	useEffect(() => {
		(async () => {
			try {
				const { data } = await getAllSites();
				setSites(data?.data?.sites);
				console.log(data.data.sites);
			} catch (err) {
				console.log('ERROR IS : ', err);
			}
			setSitesLoaded(true);
		})();
	}, []);

	/**
	 * @name filterSitesByFallbackAndSection
	 * @description
	 * This function filters sites based on the chosen filter and section:
	 * - If 'WaterFall' is selected, it includes sites where `fallbackXmlEnabled` is true.
	 * - If 'Fvp' is selected, it excludes sites where `fallbackXmlEnabled` is true.
	 * - For all other cases, it includes all sites.
	 *
	 * @param {Object} site - The site to check.
	 * @param {Object} filterValues - The selected filter option.
	 * @param {Array} filterSection - The section(s) to consider.
	 * @returns {boolean} - Returns `true` if the site meets the criteria.
	 */
	const filterSitesByFallbackAndSection = (site, filterValues, filterSection) => {
		if (filterValues.selectedFilter === 'WaterFall' && filterSection.includes('WaterFall')) {
			return site.fallbackXmlEnabled && filterValues.selectedFilter === 'WaterFall';
		}
		if (filterValues.selectedFilter === 'Fvp' && filterSection.includes('Fvp')) {
			return !site.fallbackXmlEnabled && filterValues.selectedFilter === 'Fvp';
		}
		return true;
	};

	/**
	 * @name handleInputFilter
	 * @description
	 * This function handles searching through the row data based on the input value.
	 * It checks if the `siteDomain` or `siteId` of a site includes the search value.
	 *
	 * @param {string} searchValue - The input value to search for.
	 * @param {Object} rowData - The data of the current row being checked.
	 * @returns {boolean} - Returns `true` if the `siteDomain` or `siteId` of the row includes the search value.
	 */
	const handleInputFilter = (searchValue, rowData) => {
		return (
			rowData.siteDomain.toLowerCase().includes(searchValue) ||
			rowData.siteId.toString().includes(searchValue)
		);
	};

	const handleRowClick = (data) => {
		navigate(`/fvp-editor/update-config/${data.siteId}`);
	};

	const handleSyncFvpBundle = async () => {
		try {
			const response = await syncFvpBundle();
			console.log(response);
		} catch (err) {
			console.log(err);
			const requestError = err?.response?.data?.data?.message || err.message;
			toast.error(requestError, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const handleNetworkWideSync = async () => {
		try {
			const response = await networkWideSync();
			console.log(response);
		} catch (err) {
			console.log(err);
			const requestError = err?.response?.data?.data?.message || err.message;
			toast.error(requestError, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const columnsConfig = [
		{ header: 'Site ID', key: 'siteId' },

		{
			header: 'Site Domain',
			key: 'siteDomain'
		},
		{
			header: 'Config',
			key: 'fallbackXmlEnabled',
			conditionalRendering: (row) =>
				row.fallbackXmlEnabled ? (
					<Chip color="success" label="Waterfall" variant="filled" />
				) : (
					<Chip color="info" label="Fvp" variant="filled" />
				)
		}
	];

	return (
		<Paper className={classes.dashboardMainContent}>
			<Box className={classes.pageHeadingAndButtonsContainer}>
				<PageHeading headingName="FVP Sites" headingPath={['Dashboard', 'FVP Sites']} />
			</Box>
			<Box className={classes.buttonsContainer}>
				<GeneralButton
					size="large"
					className={clsx(
						classes.circularButtonStyles,
						commonStylesClasses.minorMarginLeftAndRight
					)}
					system={SYSTEMS.FVP_PLAYER}
					variant="contained"
					fontWeight={600}
					onClick={() => navigate('/fvp-editor/create-config')}
					name="FVP_PLAYER_HOME_PAGE_NEW_SETUP_BUTTON"
				>
					+ New Setup
				</GeneralButton>
				{user.isSuperAdmin && (
					<>
						<GeneralButton
							size="large"
							className={clsx(
								classes.circularButtonStyles,
								commonStylesClasses.minorMarginLeftAndRight
							)}
							name="FVP_PLAYER_HOME_PAGE_SYNC_FVP_BUNDLE_BUTTON"
							system={SYSTEMS.FVP_PLAYER}
							variant="contained"
							fontWeight={600}
							onClick={handleSyncFvpBundle}
						>
							Sync Fvp Bundle
						</GeneralButton>

						<GeneralButton
							size="large"
							className={clsx(
								classes.circularButtonStyles,
								commonStylesClasses.minorMarginLeftAndRight
							)}
							name="FVP_PLAYER_HOME_PAGE_NETWORK_WIDE_SYNC_BUTTON"
							system={SYSTEMS.FVP_PLAYER}
							variant="contained"
							fontWeight={600}
							onClick={handleNetworkWideSync}
						>
							NetWork Wide Sync
						</GeneralButton>
					</>
				)}
			</Box>

			<Box className={classes.customTableWrapper}>
				<FilterCustomTable
					rowsData={sites ? sites : []}
					columnsConfig={columnsConfig}
					rowsPerPage={5}
					rowsPerPageOptions={[5, 10, 25]}
					dataLoaded={sitesLoaded}
					isDropDownFilterEnable={true}
					filterOptions={['WaterFall', 'Fvp', 'All']}
					filterFunction={filterSitesByFallbackAndSection}
					searchFunction={handleInputFilter}
					handleRowClick={handleRowClick}
					searchLabel="Search Site By Domain or SiteId"
				/>
			</Box>
		</Paper>
	);
};
export default FvpSiteSearch;
